import React, { useEffect, useState } from "react";
import hiddenInVatar from "../vatar/hiddenInVatar";
import { Users, Settings, Minus, Plus, Crown } from "lucide-react";
import useCoinStore from "../stores/coinStore";
import CoinBalance from "./CoinBalance";
import CoinStore from "./CoinStore";
import SubscriptionModal from "./SubscriptionModal";
import { payForProduct } from "../Services/paymentService";
import { showPurchaseProblem } from "../Services/purchaseTracker";
import usePaymentCurrency from "../hooks/usePaymentCurrency";
import usePayerDetails from "../hooks/usePayerDetails";
import PayerDetails from "./payment/PayerDetails";
import analytics from "../Services/analyticsService";
import { PRODUCTS, SEAT_BUNDLES, seatProductId, formatProductPrice } from "../Services/products";

const MAX_PLAYERS_LIMIT = 15;

const RoomUpgrade = ({
  isOpen,
  onClose,
  roomId,
  currentMaxPlayers = 5,
  onUpgrade = null
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [customAmount, setCustomAmount] = useState(1);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState("");
  const [isCoinStoreOpen, setIsCoinStoreOpen] = useState(false);
  const [showPro, setShowPro] = useState(false);
  const [fallbackProvider, setFallbackProvider] = useState(null);
  const [canTryBachs, setCanTryBachs] = useState(false);

  const { spendCoins, canAfford, syncBalance, isPro } = useCoinStore();
  const [currency, setCurrency] = usePaymentCurrency();
  const payer = usePayerDetails(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setFallbackProvider(null);
    setCanTryBachs(false);
  }, [isOpen]);

  const upgradeOptions = SEAT_BUNDLES.map((b) => ({
    id: `plus${b.seats}`,
    increase: b.seats,
    cost: b.coinCost,
    isBest: b.seats === 3,
  })).filter(option => (currentMaxPlayers + option.increase) <= MAX_PLAYERS_LIMIT);

  const maxCustomIncrease = MAX_PLAYERS_LIMIT - currentMaxPlayers;
  const customOption = {
    id: 'custom',
    increase: customAmount,
    cost: PRODUCTS[seatProductId(customAmount)]?.coinCost ?? customAmount * 100
  };

  const selectedIncrease = () =>
    !selectedOption ? 0 : selectedOption.id === 'custom' ? customOption.increase : selectedOption.increase;

  const handleUpgrade = async () => {
    if (!selectedOption) return;
    const option = selectedOption.id === 'custom' ? customOption : selectedOption;
    const newTotal = currentMaxPlayers + option.increase;

    setUpgrading(true);
    setError("");
    try {
      const result = await spendCoins(option.cost, 'room_upgrade', roomId);
      if (!result.success) throw new Error(result.message || 'Upgrade failed');
      if (onUpgrade) await onUpgrade(newTotal, option.cost);
      analytics.track("seats_purchased", { room_id: roomId, seats: option.increase, amount: option.cost, method: "coins" });
      onClose();
    } catch (err) {
      setError(err.message || "Upgrade failed. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  // Short on coins: pay the cash price for exactly these seats. Paystack is the
  // naira default; Bachs handles USD and the manual fallback path.
  const handlePayCash = async (providerOverride = fallbackProvider) => {
    setError("");
    setCanTryBachs(false);
    const details = await payer.commit();
    if (!details) return;
    setUpgrading(true);
    try {
      await payForProduct({
        currency,
        fallbackProvider: providerOverride,
        baselineMaxPlayers: currentMaxPlayers,
        productId: seatProductId(selectedIncrease()),
        email: details.email,
        name: details.name,
        roomId,
        label: "NameIt! Seats",
      });
      await syncBalance();
      onClose();
    } catch (err) {
      if (err.reference) {
        showPurchaseProblem({
          message: "Your payment went through, but we couldn't confirm it yet. Your seats should appear shortly; if they don't, email us.",
          reference: err.reference,
        });
        onClose();
      } else if (!err.cancelled) {
        const canFallback = currency === "NGN" && providerOverride !== "bachs";
        setError(
          canFallback
            ? "Paystack didn't go through. You can try another payment method."
            : err.message || "Payment didn't go through. Please try again."
        );
        setCanTryBachs(canFallback);
      }
    } finally {
      setUpgrading(false);
    }
  };

  const handleBachsFallback = () => {
    setFallbackProvider("bachs");
    handlePayCash("bachs");
  };

  const getCurrentCost = () => {
    if (!selectedOption) return 0;
    return selectedOption.id === 'custom' ? customOption.cost : selectedOption.cost;
  };

  const canAffordCurrent = () => {
    const cost = getCurrentCost();
    return canAfford(cost);
  };

  const handleBuyCoins = () => {
    console.log("Buy coins clicked");
    setIsCoinStoreOpen(true);
  };

  const handleCoinStoreClose = () => {
    setIsCoinStoreOpen(false);
    syncBalance(); // Refresh balance after purchase
  };

  if (!isOpen) return null;
  if (showPro) return <SubscriptionModal isOpen onClose={() => setShowPro(false)} />;

  if (upgradeOptions.length === 0 && maxCustomIncrease <= 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Maximum Size Reached!</h2>
            <p className="text-gray-600 mb-6">Your room can hold up to {currentMaxPlayers} players - that's the limit!</p>
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-bold text-gray-800">Add players</h2>
            <div className="flex items-center space-x-4">
              <CoinBalance 
                size="small" 
                onBuyClick={handleBuyCoins}
                showBuyButton={true}
              />
              <button
                onClick={onClose}
                disabled={upgrading}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <p className="text-gray-600 text-sm mb-6">Your room holds {currentMaxPlayers} players.</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              <p>{error}</p>
              {canTryBachs && (
                <button
                  type="button"
                  onClick={handleBachsFallback}
                  disabled={upgrading}
                  className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Try another method
                </button>
              )}
            </div>
          )}

          <div className="space-y-3 mb-4">
            {upgradeOptions.map((option) => {
              const newTotal = currentMaxPlayers + option.increase;
              const isSelected = selectedOption?.id === option.id;

              return (
                <div
                  key={option.id}
                  onClick={() => setSelectedOption(option)}
                  className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    isSelected
                      ? "border-red-600 bg-red-50"
                      : "border-gray-200 hover:border-red-300"
                  }`}
                >
                  {option.isBest && (
                    <div className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      BEST
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-bold text-gray-800">
                          +{option.increase} player{option.increase > 1 ? 's' : ''}
                        </p>
                        <p className="text-sm text-gray-600">
                          {currentMaxPlayers} → {newTotal} total
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-red-600">{option.cost} coins</p>
                  </div>
                </div>
              );
            })}

            {maxCustomIncrease > 0 && (
              <div
                onClick={() => setSelectedOption({ id: 'custom' })}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedOption?.id === 'custom'
                    ? "border-red-600 bg-red-50"
                    : "border-gray-200 hover:border-red-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Settings className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-bold text-gray-800">Customize</p>
                      <p className="text-sm text-gray-600">Add up to {MAX_PLAYERS_LIMIT - currentMaxPlayers} players</p>
                    </div>
                  </div>
                  
                  {selectedOption?.id === 'custom' && (
                    <div className="flex items-center space-x-3 bg-gray-50 rounded-lg px-3 py-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomAmount(Math.max(1, customAmount - 1));
                        }}
                        className="w-8 h-8 rounded-lg bg-white border border-gray-300 hover:border-red-400 hover:bg-red-50 flex items-center justify-center transition-colors"
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="w-8 text-center font-bold text-gray-800">{customAmount}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCustomAmount(Math.min(maxCustomIncrease, customAmount + 1));
                        }}
                        className="w-8 h-8 rounded-lg bg-white border border-gray-300 hover:border-red-400 hover:bg-red-50 flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  )}
                </div>
                
                {selectedOption?.id === 'custom' && (
                  <div className="mt-2 text-sm text-gray-600">
                    {currentMaxPlayers} → {currentMaxPlayers + customAmount} total • {customOption.cost} coins
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedOption && !canAffordCurrent() && (
            <div className="mb-4">
              <PayerDetails payer={payer} disabled={upgrading} />
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-gray-800">{getCurrentCost()} coins</p>
                {selectedOption && !canAffordCurrent() && (
                  <p className="text-sm text-gray-500">
                    or {formatProductPrice(seatProductId(selectedIncrease()), currency)}
                    {" · "}
                    <button
                      type="button"
                      onClick={() => setCurrency(currency === "USD" ? "NGN" : "USD")}
                      className="underline hover:text-gray-700"
                    >
                      pay in {currency === "USD" ? "₦" : "$"} instead
                    </button>
                  </p>
                )}
              </div>
              <button
                onClick={canAffordCurrent() ? handleUpgrade : () => handlePayCash()}
                disabled={!selectedOption || upgrading}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  !selectedOption
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : upgrading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                {upgrading
                  ? "Paying..."
                  : selectedOption && !canAffordCurrent()
                  ? `Pay ${formatProductPrice(seatProductId(selectedIncrease()), currency)}`
                  : "Pay"}
              </button>
            </div>
          </div>

          {!isPro && (
            <button
              onClick={() => {
                setShowPro(true);
                analytics.track("pro_upsell_opened", { source: "add_seats_sheet" });
              }}
              className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm text-amber-700 hover:text-amber-800"
            >
              <Crown className="w-4 h-4" />
              Always hosting? <span className="font-semibold">Try Pro →</span>
            </button>
          )}
        </div>
      </div>

      <CoinStore isOpen={isCoinStoreOpen} onClose={handleCoinStoreClose} />
    </div>
  );
};

export default hiddenInVatar(RoomUpgrade);
